import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSavedTask } from '@webapp/account/src/hooks/use-saved-task';
import { ResultsContext } from '@webapp/account/src/pages/survey/results/context';
import {
    selectAudioCount,
    selectAudioSize,
    selectFilesCount,
    selectFilesSize,
    selectReportsList,
    selectWithFilters,
} from '@webapp/account/src/resources/reports/selectors';
import {
    selectTotalAudioCount,
    selectTotalAudioSize,
    selectTotalFileCount,
    selectTotalFileSize,
} from '@webapp/account/src/resources/survey/selectors';
import { FileType, TaskLocalStatus, TaskType } from '@webapp/account/src/resources/tasks/task';

import { crc32 } from '@webapp/common/lib/crc32';
import { LocalStorage } from '@webapp/common/lib/storage';
import { QuestionType } from '@webapp/common/resources/survey';

import { FiltersSwitch } from '../filters-switch';
import { Footer } from '../footer';

import { Capacity } from './capacity';
import { CountAndDelete } from './stat';

import css from './files.css';

const Title: FC = () => <div className={css.title}>Вы можете скачать загруженные файлы одним архивом</div>;

export const Files: FC<{
    surveyId: number;
    audio?: boolean;
    isPublic?: boolean;
}> = ({ audio, surveyId, isPublic }) => {
    const { globalFilters } = useContext(ResultsContext);
    const filtersEnabled = useSelector(selectWithFilters);
    const [useFilters, setUseFilters] = useState(filtersEnabled);
    const filtersHash = useFilters ? crc32(JSON.stringify(globalFilters)) : 101;
    const audioSize = useSelector(selectAudioSize);
    const audioCount = useSelector(selectAudioCount);
    const totalFileSize = useSelector(selectTotalFileSize);
    const totalFileCount = useSelector(selectTotalFileCount);
    const totalAudioSize = useSelector(selectTotalAudioSize);
    const totalAudioCount = useSelector(selectTotalAudioCount);
    const filesSizeFilters = useSelector(selectFilesSize);
    const filesCountFilters = useSelector(selectFilesCount);
    let size = useFilters ? filesSizeFilters : totalFileSize;
    let count = useFilters ? filesCountFilters : totalFileCount;

    if (audio) {
        // TODO refactor
        size = useFilters ? audioSize : totalAudioSize;
        count = useFilters ? audioCount : totalAudioCount;
    }

    const {
        complete,
        createTask,
        forget,
        link,
        progress,
        size: downloadSize,
        status,
        taskStatus,
    } = useSavedTask(filtersHash, `s_${surveyId}`);
    const reports = useSelector(selectReportsList);
    const waiting = status === TaskLocalStatus.LOADING;

    // TODO purge questions, pick in partial loading
    const questions = useMemo(
        () => reports.filter(({ type }) => type === QuestionType.FILE_UPLOAD).map(({ question }) => question.id),
        [reports],
    );

    const handleClickDownload = useCallback(() => {
        createTask({
            surveyId,
            audio,
            fileType: FileType.PDF,
            taskType: TaskType.ARCH,
            globalFilters,
            questions,
        });
    }, [createTask, surveyId, audio, globalFilters, questions]);

    const handleWithFilters = useCallback(
        (enabled: boolean) => {
            setUseFilters(enabled);
            LocalStorage.set(`survey_${surveyId}_use_filters`, enabled);
        },
        [surveyId],
    );

    const onDownload = useCallback(() => forget(), [forget]);

    useEffect(() => {
        const withFilters = LocalStorage.get<boolean>(`survey_${surveyId}_use_filters`);
        if (withFilters) {
            setUseFilters(withFilters);
        }
    }, [surveyId]);

    return (
        <div className={css.files}>
            <Title />
            <div className={css.info}>
                <Capacity size={size} />
                <div className={css.main}>
                    <CountAndDelete
                        filesCount={count}
                        surveyId={surveyId}
                        withFilters={useFilters}
                        isPublic={isPublic}
                    />
                    <FiltersSwitch
                        defaultChecked={useFilters}
                        disabled={!filtersEnabled}
                        label='С учетом фильтров'
                        onChange={handleWithFilters}
                    />
                </div>
            </div>
            <Footer
                complete={complete}
                disabled={count === 0}
                link={link}
                loading={waiting}
                progress={progress}
                size={downloadSize}
                taskStatus={taskStatus}
                onClick={handleClickDownload}
                onDownload={onDownload}
            />
        </div>
    );
};
